import React, { useContext, useEffect } from "react";
import { createContext, ReactNode, useState } from "react";
import useCellTab from "../hooks/useCellTab";
import { ForecastContext } from "./ForecastContext";
import { ShipmentHeaderContext } from "./ShipmentHeaderContext";
import { HorizontalHeaders } from "../pages/components/FuturesTable/utils/header.utils";

export const AtOnceContext = createContext<{
  atOnceData: number[][];
  setAtOnceData: (val: number[][]) => void;
  selectedCell: { row: number; column: number };
  setNextCell: (row: number, column: number) => () => void;
}>({
  atOnceData: [[1]],
  setAtOnceData: (val) => val,
  selectedCell: { row: -1, column: -1 },
  setNextCell: (row, column) => () => row + column,
});

export const createAtOnceDefaultData = (horizontalHeaders: HorizontalHeaders) =>
  Array(1)
    .fill(0)
    .map((_) => Array(horizontalHeaders.MONTHS.length + 1).fill(0));

export const createAtOnceNVSDefaultData = (
  horizontalHeaders: HorizontalHeaders,
) =>
  Array(4)
    .fill(0)
    .map((_) => Array(horizontalHeaders.MONTHS.length + 1).fill(0));
export const AT_ONCE_COLUMNS = [
  "At Once Shipments",
  "NFS ReBuys Shipment",
  "Close Outs",
  "At Once Total",
];
export const AtOnceProvider = ({
  children,
  pathname,
}: {
  children: ReactNode;
  pathname: string;
}) => {
  const isNVS = pathname.includes("nvs");
  const { horizontalHeaders } = useContext(ShipmentHeaderContext);
  const { originalForecast } = useContext(ForecastContext);
  const atOnceDefaultData = createAtOnceDefaultData(horizontalHeaders);
  const AT_ONCE_NVS_DEFAULT_DATA =
    createAtOnceNVSDefaultData(horizontalHeaders);
  const loadData = isNVS ? AT_ONCE_NVS_DEFAULT_DATA : atOnceDefaultData;
  const [atOnceData, setAtOnceData] = useState<number[][]>(loadData);
  const { selectedCell, setNextCell } = useCellTab(
    horizontalHeaders.MONTHS.length,
    loadData.length,
  );
  useEffect(() => {
    if (originalForecast.at_once && originalForecast.at_once[0].length > 1) {
      const newAtOnceData = originalForecast.at_once.map((forecast) => {
        return forecast
          .map((month) => month)
          .concat(forecast.reduce((a, b) => a + b, 0));
      });
      if (isNVS) {
        const totals = newAtOnceData.reduce((acc, row) => {
          if (acc.length === 0) return row;
          return acc.map((val, i) => val + row[i]);
        }, []);
        newAtOnceData.push(totals);
      }
      setAtOnceData(isNVS ? newAtOnceData : [newAtOnceData[0]]);
    }
  }, [originalForecast]);
  return (
    <AtOnceContext.Provider
      value={{ atOnceData, setAtOnceData, selectedCell, setNextCell }}
    >
      {children}
    </AtOnceContext.Provider>
  );
};
